/**
 * Generates time axis values aligned to round intervals
 * @param min - Minimum timestamp (in milliseconds)
 * @param max - Maximum timestamp (in milliseconds)
 * @param interval - Interval in milliseconds (e.g., 10 * 60 * 1000 for 10 minutes)
 * @returns Array of timestamps at round interval boundaries
 */
export const generateTimeAxisValues = (min: number, max: number, interval: number): number[] => {
  const values: number[] = [];
  const startOfDay = new Date(min).setHours(0, 0, 0, 0);
  let current = startOfDay + Math.ceil((min - startOfDay) / interval) * interval;
  while (current <= max) {
    values.push(current);
    current += interval;
  }
  return values;
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Generates one axis value per calendar day covered by [min, max], for the
 * date header row above the chart - unlike generateTimeAxisValues, this
 * never produces more than one tick per day regardless of how fine the
 * chart's time-of-day grid interval is.
 * @param min - Minimum timestamp (in milliseconds)
 * @param max - Maximum timestamp (in milliseconds)
 * @returns Array of timestamps, one per calendar day
 */
export const generateDayAxisValues = (min: number, max: number): number[] => {
  const values: number[] = [];
  const startOfFirstDay = new Date(min).setHours(0, 0, 0, 0);

  // The chart's visible range usually starts mid-day, so the first day's
  // own midnight falls before `min` (off the left edge) - anchor that
  // label to the actual start of the chart instead of a point that isn't
  // on the visible axis.
  values.push(Math.max(startOfFirstDay, min));

  let current = startOfFirstDay + ONE_DAY_MS;
  while (current <= max) {
    values.push(current);
    current += ONE_DAY_MS;
  }

  return values;
};

export const MAX_TIME_AXIS_LABELS = 80;

/**
 * Thins out a dense list of axis tick values so at most `maxLabels` of them
 * are kept, by stepping through at the smallest stride that gets under the
 * cap (every 2nd value, then every 3rd, etc.) rather than dropping down to a
 * fixed label count - this keeps the label spacing even instead of clumping.
 * Returns `values` unchanged when it's already at or under `maxLabels`.
 * @param values - Full, evenly-spaced list of axis tick timestamps
 * @param maxLabels - Maximum number of labels to keep (default 80)
 */
export const subsampleAxisValues = (values: number[], maxLabels: number = MAX_TIME_AXIS_LABELS): number[] => {
  if (values.length <= maxLabels) {
    return values;
  }

  const stride = Math.ceil(values.length / maxLabels);
  return values.filter((_, index) => index % stride === 0);
};
